import React from 'react';
import {
  Button,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
} from '@mui/material';
import {
  Download,
  Share,
  Print,
  ContentCopy,
  Lock,
} from '@mui/icons-material';

export const InvoiceActions = ({
  onDownloadPDF,
  onPrintInvoice,
  pdfGenerating,
  onCopyInvoiceNumber,
  onShareInvoice,
  invoiceNumber,
  invoiceStatus,
  createdDate,
  getStatusText,
  getStatusColor,
  formatDate,
  variant = 'creator', // 'creator' or 'regular'
  isAuthenticated = false, // NEW: Authentication status
}) => {
  if (variant === 'creator') {
    return (
      <>
        <Card sx={{ mb: 2.5, borderRadius: 3 }}>
          <CardContent sx={{ py: 2, px: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Invoice #{invoiceNumber}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={getStatusText(invoiceStatus)}
                    color={getStatusColor(invoiceStatus)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Created {formatDate(createdDate)}
                  </Typography>
                </Stack>
              </Box>

              <Stack direction="row" spacing={1}>
                <Tooltip title="Copy Invoice Number">
                  <IconButton onClick={onCopyInvoiceNumber} size="small">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="outlined"
                  startIcon={<Share />}
                  size="small"
                  onClick={onShareInvoice}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  Share
                </Button>

                {/* Download Button - Only active if authenticated */}
                {isAuthenticated ? (
                  <Button
                    variant="outlined"
                    startIcon={pdfGenerating ? <CircularProgress size={16} /> : <Download />}
                    onClick={onDownloadPDF}
                    disabled={pdfGenerating}
                    size="small"
                  >
                    {pdfGenerating ? 'Generating...' : 'Download PDF'}
                  </Button>
                ) : (
                  <Tooltip title="Sign in to download invoice PDF">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<Lock />}
                        disabled
                        size="small"
                        sx={{
                          opacity: 0.6,
                        }}
                      >
                        Download PDF
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {/* Authentication Warning - Only show if not authenticated */}
            {!isAuthenticated && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Lock fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Sign in to download this invoice as a PDF
                  </Typography>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </>
    );
  }

  // Regular invoice variant
  return (
    <>
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          {/* Download Button - Only active if authenticated */}
          {isAuthenticated ? (
            <Button
              variant="contained"
              startIcon={pdfGenerating ? <CircularProgress size={16} /> : <Download />}
              onClick={onDownloadPDF}
              disabled={pdfGenerating}
              sx={{
                background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
                px: 3,
                py: 1.5,
              }}
            >
              {pdfGenerating ? 'Generating...' : 'Download PDF'}
            </Button>
          ) : (
            <Tooltip title="Sign in to download invoice PDF">
              <span>
                <Button
                  variant="contained"
                  startIcon={<Lock />}
                  disabled
                  sx={{
                    background: 'linear-gradient(45deg, #64748b 30%, #94a3b8 90%)',
                    px: 3,
                    py: 1.5,
                    opacity: 0.7,
                  }}
                >
                  Download PDF
                </Button>
              </span>
            </Tooltip>
          )}

          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={onPrintInvoice || (() => window.print())}
            sx={{
              borderColor: '#cbd5e1',
              color: '#334155',
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#ffffff',
                color: '#0f172a',
              }
            }}
          >
            Print
          </Button>

          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={onShareInvoice}
            sx={{
              borderColor: '#cbd5e1',
              color: '#334155',
              backgroundColor: 'rgba(255,255,255,0.9)',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#ffffff',
                color: '#0f172a',
              }
            }}
          >
            Share
          </Button>
        </Stack>

        {/* Authentication Warning - Only show if not authenticated */}
        {!isAuthenticated && (
          <Alert 
            severity="info" 
            icon={<Lock />}
            sx={{ 
              mt: 2, 
              maxWidth: 600, 
              mx: 'auto',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <Typography variant="body2">
              <strong>Sign in required:</strong> You must be logged in to download this invoice as a PDF.
            </Typography>
          </Alert>
        )}
      </Box>
    </>
  );
};

export default InvoiceActions;
